import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { CONTACT_FORM_ENDPOINT, FORM_TIMEOUT } from '../utils/config';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// Security: Warn if reCAPTCHA is not configured
if (!RECAPTCHA_SITE_KEY && process.env.NODE_ENV === 'production') {
  console.warn('Security Warning: REACT_APP_RECAPTCHA_SITE_KEY is not configured. Form is vulnerable to spam.');
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate reCAPTCHA (required if configured)
    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setRecaptchaError(true);
      return;
    }
    setRecaptchaError(false);

    setSubmitStatus('loading');
    setErrorMessage('');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FORM_TIMEOUT);

      const response = await fetch(CONTACT_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ ...formData, recaptchaToken }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check if the response was successful
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      setSubmitStatus('success');

      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        service: '',
        message: '',
      });
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();

    } catch (error) {
      // Only log in development to avoid exposing error details in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission error:', error);
      }

      if (error instanceof Error && error.name === 'AbortError') {
        setErrorMessage('Request timeout. Please try again.');
      } else {
        setErrorMessage('Please try again or email us directly at hello@smpldigital.co.uk');
      }

      setSubmitStatus('error');

      // Reset error message after 8 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
        setErrorMessage('');
      }, 8000);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-20 bg-neutral-50 dark:bg-neutral-dark-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-neutral-900 dark:text-neutral-dark-900 mb-4">
            Let's Talk About Your Goals
          </h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-dark-600 max-w-3xl">
            Tell us about your challenge. We'll get back to you within 24 hours with some initial thoughts.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="fade-in-up">
            <div className="bg-white dark:bg-neutral-dark-200 rounded-xl shadow-lg p-8 border border-neutral-200 dark:border-neutral-dark-300">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-neutral-700 dark:text-neutral-dark-700 mb-2"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-dark-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-indigo focus:border-transparent bg-white dark:bg-neutral-dark-100 text-neutral-900 dark:text-neutral-dark-900 transition-all"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-neutral-700 dark:text-neutral-dark-700 mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-dark-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-indigo focus:border-transparent bg-white dark:bg-neutral-dark-100 text-neutral-900 dark:text-neutral-dark-900 transition-all"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="service"
                    className="block text-sm font-medium text-neutral-700 dark:text-neutral-dark-700 mb-2"
                  >
                    What can we help you with?
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-dark-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-indigo focus:border-transparent bg-white dark:bg-neutral-dark-100 text-neutral-900 dark:text-neutral-dark-900 transition-all"
                  >
                    <option value="">Select a service</option>
                    <option value="seo">SEO - Improve search rankings</option>
                    <option value="website">Website - Build or redesign</option>
                    <option value="ai-automation">AI & Automation - Streamline operations</option>
                    <option value="hubspot">HubSpot - CRM & marketing</option>
                    <option value="custom-apps">Custom Apps - Bespoke solutions</option>
                    <option value="ppc">PPC - Paid advertising</option>
                    <option value="content">Content Creation</option>
                    <option value="not-sure">Not sure yet</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-neutral-700 dark:text-neutral-dark-700 mb-2"
                  >
                    Tell us about your challenge
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-dark-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-indigo focus:border-transparent bg-white dark:bg-neutral-dark-100 text-neutral-900 dark:text-neutral-dark-900 transition-all resize-none"
                    placeholder="What results are you looking to achieve?"
                  />
                </div>

                {/* reCAPTCHA */}
                {RECAPTCHA_SITE_KEY && (
                  <div>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={(token) => {
                        setRecaptchaToken(token);
                        setRecaptchaError(false);
                      }}
                      onExpired={() => setRecaptchaToken(null)}
                    />
                    {recaptchaError && (
                      <p className="mt-2 text-sm text-semantic-error dark:text-semantic-errorDark">
                        Please complete the reCAPTCHA verification.
                      </p>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className="btn-hover w-full px-6 py-4 bg-primary-rose text-white rounded-lg font-semibold text-lg hover:bg-primary-rose/90 focus:outline-none focus:ring-2 focus:ring-primary-rose focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitStatus === 'loading' ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Start the Conversation'
                  )}
                </button>

                {/* Form Status - aria-live so screen readers announce changes */}
                <div aria-live="polite" aria-atomic="true">
                  {submitStatus === 'success' && (
                    <div className="status-appear mt-4 p-4 bg-accent-emerald/10 border border-accent-emerald/30 rounded-lg" role="status">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-accent-emerald mr-3 flex-shrink-0 mt-0.5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-accent-emerald mb-1">Message sent successfully!</h4>
                          <p className="text-sm text-neutral-700 dark:text-neutral-dark-700">
                            We'll get back to you within 24 hours at the email address you provided.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="status-appear mt-4 p-4 bg-semantic-errorLight dark:bg-semantic-error/20 border border-semantic-errorBorder dark:border-semantic-error/40 rounded-lg" role="alert">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-semantic-error dark:text-semantic-errorDark mr-3 flex-shrink-0 mt-0.5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-semantic-error dark:text-semantic-errorDark mb-1">Unable to send message</h4>
                          <p className="text-sm text-semantic-errorMuted dark:text-semantic-errorDark">
                            {errorMessage || 'Please try again or email us directly at hello@smpldigital.co.uk'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Contact Info & CTA */}
          <div className="space-y-8">
            {/* Contact Information */}
            <div className="bg-white dark:bg-neutral-dark-200 rounded-xl shadow-lg p-8 border border-neutral-200 dark:border-neutral-dark-300 fade-in-up animation-delay-200">
              <h3 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-dark-900 mb-6">
                Get in Touch
              </h3>

              <div className="space-y-4">
                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-primary-indigo mr-4 flex-shrink-0"
                    aria-hidden="true"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="text-neutral-700 dark:text-neutral-dark-700">
                    hello@smpldigital.co.uk
                  </span>
                </div>

                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-primary-indigo mr-4 flex-shrink-0"
                    aria-hidden="true"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <a
                    href="tel:+441798672673"
                    className="text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                  >
                    +44 (0)1798 672 673
                  </a>
                </div>

                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-primary-indigo mr-4 flex-shrink-0"
                    aria-hidden="true"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-neutral-700 dark:text-neutral-dark-700">
                    Remote & On-site Services
                  </span>
                </div>

                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-primary-indigo mr-4 flex-shrink-0"
                    aria-hidden="true"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-neutral-700 dark:text-neutral-dark-700">
                    We respond within 24 hours
                  </span>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-br from-primary-indigo to-primary-indigoLight rounded-xl p-8 text-white shadow-xl fade-in-up animation-delay-300">
              <h3 className="text-2xl font-bold mb-4">No Obligation. Just a Conversation.</h3>
              <p className="mb-6 leading-relaxed">
                We'll discuss your goals, explore what's possible, and share initial thoughts on how we can help—no pressure, no sales pitch.
              </p>
              <p className="text-sm opacity-90">
                Most clients see a clear path forward after our first conversation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
